import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Button,
  Spin,
  Input,
  Alert,
  Radio,
  DatePicker,
  Table,
  Tooltip,
  Empty,
  message,
} from 'antd';
import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { ColumnsType } from 'antd/lib/table';
import { storage, renderIntlLabel } from '@/utils';
import ContentBlock, { FormItem } from '@/components/Reservation/ContentBlock';
import IconFont from '@/components/IconFont';
import { parseFen2Currency } from '@/utils/number';
import CustomDrawer, { IdrawerRef } from '@/components/CustomDrawer';
import NumberWithHelper from '@/components/Reservation/NumberInput/InputWithHelper';
import {
  queryInviteMemberInfo,
  queryDayRoomListV3,
  queryTimeRoomListV3,
  queryInvitePrepare,
} from '@/api/crsInvitation';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '@/config';
import { getTimeByUtcOffset } from '@/utils/intl';
import ConfirmOrderModal, { ConfirmModalRef } from './ConfirmOrderModal';
import { OccType } from '../constant';

import styles from './index.module.scss';

const NUM_REG = /^-?\d*(\.\d*)?$/;
const phoneNumberPattern = /^1[3-9]\d{9}$/;
const crsPromotionList = [
  {
    type: 'DAY_SPECIAL',
    label: renderIntlLabel('channel_label_DAY_SPECIAL'),
    value: 0,
  },
  {
    type: 'NEW_CUSTOMER_DISCOUNT',
    label: renderIntlLabel('channel_label_NEW_CUSTOMER_DISCOUNT'),
    value: 0,
  },
  {
    type: 'BUY_LIMIT',
    label: renderIntlLabel('channel_label_BUY_LIMIT'),
    value: 0,
  },
  {
    type: 'CONNECTED_DISCOUNT',
    label: renderIntlLabel('channel_label_CONNECTED_DISCOUNT'),
    value: 0,
  },
  {
    type: 'ADVANCE_BOOKING',
    lebal: renderIntlLabel('channel_label_ADVANCE_BOOKING'),
    value: 0,
  },
  {
    type: 'TONIGHT_SALE',
    label: renderIntlLabel('channel_label_TONIGHT_SALE'),
    value: 0,
  },
];

const InvitationDetailDrawer: React.FC<any> = ({ userInfo, init, myRef }) => {
  const commonCode = storage.get('commonCode');
  const drawerRef = useRef<IdrawerRef>();
  const confirmModalRef = useRef<ConfirmModalRef>();
  const [guestPhone, setGuestPhone] = useState<string>('');
  const [occType, setOccType] = useState<string>('DAILY');
  const [rentTimeNum, setRentTimeNum] = useState<number>(1);
  const [guestName, setGuestName] = useState<string>('');
  const [memberInfo, setMemberInfo] = useState<any>({});
  const [selecteDate, setSelecteDate] = useState<Moment>(moment());
  const [dataDayList, setDataDayList] = useState<any>([]);
  const [dataHoursList, setDataHoursList] = useState<any>([]);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [memberInfoLoading, setMemberInfoLoading] = useState<boolean>(false);

  const resetValue = () => {
    setGuestPhone('');
    setGuestName('');
    setMemberInfo({});
    setRentTimeNum(1);
    setSelecteDate(moment());
    setOccType('DAILY');
    setDataDayList([]);
    setDataHoursList([]);
  };

  const handleCancel = () => {
    drawerRef.current.closeDrawer();
    resetValue();
    if (init) {
      init();
    }
  };

  const handleOpenDrawer = () => {
    setSelecteDate(moment());
    drawerRef.current.openDrawer();
  };

  const getInviteMemberInfo = async () => {
    setMemberInfoLoading(true);
    try {
      const { data, code } = await queryInviteMemberInfo({
        guestName,
        guestPhone,
      });
      if (code === 0) {
        setMemberInfo(data);
        setMemberInfoLoading(false);
      }
    } catch (error) {
      setMemberInfoLoading(false);
    }
  };

  const getDayRoomList = async () => {
    setTableLoading(true);
    let headers: any = {
      wyuid: memberInfo.wyuid,
    };
    if (memberInfo.custNo) {
      headers = {
        ...headers,
        custNo: memberInfo.custNo,
      };
    }
    const { data, code } = await queryDayRoomListV3(
      {
        checkInTime: getTimeByUtcOffset(selecteDate, DATE_TIME_FORMAT),
        checkOutTime: `${getTimeByUtcOffset(
          moment(selecteDate).add(rentTimeNum, 'days'),
          DATE_FORMAT,
        )} 12:00:00`,
        commonCode,
      },
      headers,
    );
    if (code === 0) {
      const newList = [];
      data.forEach((item) => {
        item.productInfoList.forEach((val) => {
          newList.push(val);
        });
      });

      setDataDayList(newList);
      setTableLoading(false);
    }
  };

  const getTimeRoomList = async () => {
    setTableLoading(true);
    let headers: any = {
      wyuid: memberInfo.wyuid,
    };
    if (memberInfo.custNo) {
      headers = {
        ...headers,
        custNo: memberInfo.custNo,
      };
    }
    const { data, code } = await queryTimeRoomListV3(
      {
        checkInTime: getTimeByUtcOffset(selecteDate, DATE_FORMAT),
        commonCode,
      },
      headers,
    );
    if (code === 0) {
      setDataHoursList(data);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    if (guestPhone) {
      if (occType === OccType.DAILY) {
        getDayRoomList();
      } else {
        getTimeRoomList();
      }
    }
  }, [occType, memberInfo, selecteDate, rentTimeNum]);

  useImperativeHandle(
    myRef,
    () => ({
      ...drawerRef.current,
      openDrawer: handleOpenDrawer,
    }),
    [],
  );

  const handleInputBlur = () => {
    if (guestName && guestPhone) {
      if (phoneNumberPattern.test(guestPhone)) {
        getInviteMemberInfo();
      } else {
        message.error(renderIntlLabel('channel_msg_mobile_format_error'));
      }
    }
  };

  // 输入回调
  const handleChange = (event) => {
    const inputVal = event.target.value;
    if (
      (!Number.isNaN(inputVal) && NUM_REG.test(inputVal)) ||
      inputVal === '' ||
      inputVal === '-'
    ) {
      setGuestPhone(inputVal);
      if (inputVal.length === 11) {
        if (phoneNumberPattern.test(inputVal)) {
          if (occType === OccType.DAILY) {
            getDayRoomList();
          } else {
            getTimeRoomList();
          }
        } else {
          message.error(renderIntlLabel('channel_msg_mobile_format_error'));
        }
      }
    }
  };

  const handleOCCTypeChange = (e) => {
    setOccType(e.target.value);
  };

  const handleDayDurationChange: (value: string) => void = (value) => {
    let rentTime;
    if (value === null) {
      rentTime = 1;
    } else {
      if (!/^\d*$/.test(value)) return;
      if (value) {
        rentTime = Number(value);
        // 日租房 1-180天
        if (rentTime < 1 || rentTime > 180) return;
      }
    }
    setRentTimeNum(rentTime);
    // console.log(rentTime);
  };

  const getDayPreOutItem = () => {
    if (moment(selecteDate).format('HH') < '06') {
      return `${getTimeByUtcOffset(
        rentTimeNum > 1 ? moment(selecteDate).add(rentTimeNum - 1, 'days') : moment(selecteDate),
        DATE_FORMAT,
      )} 12:00:00`;
    }
    return `${getTimeByUtcOffset(
      moment(selecteDate).add(rentTimeNum, 'days'),
      DATE_FORMAT,
    )} 12:00:00`;
  };

  const handleConfirmOrderModal = (record) => {
    // confirmOrderRef.current.openDrawer();

    const obj: any = {};
    if (occType === OccType.DAILY) {
      obj.checkOutTime = getDayPreOutItem();
      obj.checkInTime = getTimeByUtcOffset(selecteDate, DATE_TIME_FORMAT);

      obj.rentTimeNum = rentTimeNum;
      obj.roomTypeId = record.roomTypeId;
    }

    if (occType === OccType.HOUR) {
      obj.checkInTime = getTimeByUtcOffset(selecteDate, DATE_TIME_FORMAT);
      obj.dayRoomInTime = record.dayRoomInTime;
      obj.dayRoomOutTime = record.dayRoomOutTime;
      obj.roomType = 'HOUR';
      obj.hours = record.hours;
      obj.roomTypeId = record.roomId;
    }
    confirmModalRef.current.showModal({
      productName: record.productName,
      guestPhone,
      guestName,
      occType,
      wyuid: memberInfo.wyuid,
      custNo: memberInfo.custNo,
      userId: memberInfo.userId,

      productId: record.productId,
      roomQuantity: record.roomQuantity,
      userInfo,
      selecteDate,
      type: 'add',
      ...obj,
    });
  };

  const handleConfirm = () => {
    handleCancel();
  };

  const disabledDate = (currentDate: Moment) => {
    const startDate = moment();
    if (currentDate.isSameOrAfter(startDate, 'day')) {
      return false;
    }
    return true;
  };

  const handleDateChange = (date: Moment) => {
    setSelecteDate(date);
  };

  const getCustNo = (custNo) => {
    if (custNo) {
      const reg = /^(.{4})(?:\d+)(.{4})$/;
      const newCustNo = custNo.replace(reg, '$1**********$2');
      return newCustNo;
    }
    return '';
  };

  const getDiscountAmount = (list, type) => {
    let discountAmountValue = 0;
    list.forEach((val) => {
      if (val.discountType === type) {
        discountAmountValue = val.discountAmount;
      }
    });
    return parseFen2Currency(discountAmountValue) || '/';
  };

  const getcrsPromotionType = (list) => {
    const arr = [];
    const domArr = [];

    list.forEach((val) => {
      crsPromotionList.forEach((item) => {
        if (val.discountType === item.type) {
          arr.push({
            ...item,
            value: val.discountAmount,
          });
        }
      });
    });

    arr.forEach((v) => {
      const str = `${v.label}:${parseFen2Currency(v.value)}`;
      domArr.push(str);
    });
    return domArr;
  };

  const columnsDay: ColumnsType<any> = [
    {
      title: renderIntlLabel('channel_label_crs_productName'),
      dataIndex: 'productName',
      width: 200,
    },
    {
      title: renderIntlLabel('channel_label_avalible_booking_roomQuantity'),
      dataIndex: 'roomQuantity',
      width: 100,
    },
    {
      title: renderIntlLabel('channel_label_market_price'),
      dataIndex: 'greyPrice',
      width: 100,
      render: (val) => {
        return parseFen2Currency(val) || '/';
      },
    },
    {
      title: renderIntlLabel('channel_label_memberDiscount'),
      dataIndex: 'memberDiscount',
      width: 100,
      render: (_val, record) => {
        return record?.discountInfo?.discountInfoList
          ? getDiscountAmount(record?.discountInfo?.discountInfoList, 'memberDiscount')
          : '/';
      },
    },
    {
      title: renderIntlLabel('channel_label_promotion_discount'),
      dataIndex: 'discountInfoList',
      width: 200,
      render: (_val, record) => {
        const arr = getcrsPromotionType(record?.discountInfo?.discountInfoList);
        if (arr.length > 0) {
          return (
            <div>
              {arr.map((item) => {
                return <p key={item}>{item}</p>;
              })}
            </div>
          );
        }
        return '/';
      },
    },
    {
      title: renderIntlLabel('channel_label_couponDiscount'),
      dataIndex: 'couponDiscount',
      width: 100,
      render: (_val, record) => {
        return record?.discountInfo?.discountInfoList
          ? getDiscountAmount(record?.discountInfo?.discountInfoList, 'couponDiscount')
          : '/';
      },
    },
    {
      title: () => {
        return (
          <Tooltip placement="top" title={renderIntlLabel('channel_title_sale_price_by_crs_rule')}>
            {renderIntlLabel('channel_label_sale_price')}
            <IconFont type="icon-tishi" className={styles.countTipIcon} />
          </Tooltip>
        );
      },
      dataIndex: 'leastMoney',
      width: 150,
      render: (val) => {
        return parseFen2Currency(val);
      },
    },
    {
      title: renderIntlLabel('order_action'),
      width: 150,
      render: (_v, record) => {
        return (
          <div>
            {record.orderFlag ? (
              <span
                onClick={() => {
                  if (guestName) {
                    handleConfirmOrderModal(record);
                  } else {
                    message.error(
                      renderIntlLabel('order_roomorder_placeholder_fill_in_subscriber'),
                    );
                  }
                }}
                className={styles.reservationBtn}
              >
                {renderIntlLabel('order_action_reservation')}
              </span>
            ) : (
              <span className={styles.fullHouseBtn}>
                {renderIntlLabel('channel_label_full_room')}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  const columnsHours: ColumnsType<any> = [
    {
      title: renderIntlLabel('channel_label_crs_productName'),
      dataIndex: 'roomTypeName',
      width: 200,
    },
    {
      title: renderIntlLabel('channel_label_avalible_booking_roomQuantity'),
      dataIndex: 'roomQuantity',
      width: 100,
    },
    {
      title: renderIntlLabel('channel_label_market_price'),
      dataIndex: 'greyPrice',
      width: 100,
      render: (val) => {
        return parseFen2Currency(val);
      },
    },
    {
      title: renderIntlLabel('channel_label_memberDiscount'),
      dataIndex: 'memberDiscount',
      width: 100,
      render: (_val, record) => {
        return record?.discountInfo?.discountInfoList
          ? getDiscountAmount(record?.discountInfo?.discountInfoList, 'memberDiscount')
          : '/';
      },
    },
    {
      title: renderIntlLabel('channel_label_couponDiscount'),
      dataIndex: 'couponDiscount',
      width: 200,
      render: (_val, record) => {
        return record?.discountInfo?.discountInfoList
          ? getDiscountAmount(record?.discountInfo?.discountInfoList, 'couponDiscount')
          : '/';
      },
    },
    {
      title: () => {
        return (
          <Tooltip placement="top" title={renderIntlLabel('channel_title_sale_price_by_crs_rule')}>
            {renderIntlLabel('channel_label_sale_price')}
            <IconFont type="icon-tishi" className={styles.countTipIcon} />
          </Tooltip>
        );
      },
      dataIndex: 'leastMoney',
      width: 150,
      render: (val) => {
        return parseFen2Currency(val);
      },
    },
    {
      title: renderIntlLabel('order_action'),
      width: 150,
      render: (_v, record) => {
        return (
          <div>
            {record.orderFlag ? (
              <span
                onClick={() => {
                  if (guestName) {
                    handleConfirmOrderModal(record);
                  } else {
                    message.error(
                      renderIntlLabel('order_roomorder_placeholder_fill_in_subscriber'),
                    );
                  }
                }}
                className={styles.reservationBtn}
              >
                {renderIntlLabel('order_action_reservation')}
              </span>
            ) : (
              <span className={styles.fullHouseBtn}>
                {renderIntlLabel('channel_label_full_room')}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <CustomDrawer
        ref={drawerRef}
        title={renderIntlLabel('order_crs_action_create_invite_order')}
        footer={
          <>
            <Button onClick={handleCancel}>{renderIntlLabel('order_action_close')}</Button>
          </>
        }
        width={1280}
        onClose={handleCancel}
      >
        <div className={styles.container}>
          <div className="space-between">
            <div className="groupLabel">{renderIntlLabel('order_roomorder_subscriber_info')}</div>
          </div>
          <Alert
            message={renderIntlLabel('order_crs_msg_create_order_by_wyuid')}
            type="warning"
            showIcon
            style={{ marginTop: '16px' }}
          />
          <Spin spinning={memberInfoLoading}>
            <div className={styles.reservationInfo}>
              {/* <div className={styles.reservationInfoItem}>
              <span className={styles.label}>预订人</span>
            </div> */}
              <FormItem label={renderIntlLabel('order_roomorder_subscriber')}>
                <Input
                  maxLength={10}
                  placeholder={renderIntlLabel('order_roomorder_placeholder_input_subscriber')}
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  onBlur={() => handleInputBlur()}
                />
              </FormItem>
              <FormItem label={renderIntlLabel('order_roomorder_mobile')}>
                <Input
                  maxLength={11}
                  value={guestPhone}
                  placeholder={renderIntlLabel('order_roomorder_placeholder_input_mobile')}
                  onChange={(e) => handleChange(e)}
                  onBlur={() => handleInputBlur()}
                />
              </FormItem>
              <FormItem label={renderIntlLabel('order_roomorder_cert_no')}>
                {getCustNo(memberInfo.custNo) || ''}
              </FormItem>
              <FormItem label={renderIntlLabel('order_roomorder_member_level')}>
                {memberInfo.memberLevelName || ''}
              </FormItem>
            </div>
          </Spin>
          {/* <Alert
            message="按手机号对应的网鱼账户下预订单，请和顾客确认后填写"
            type="info"
            showIcon
            style={{ marginBottom: '16px', marginTop: '16px' }}
          /> */}
          <div className="space-between">
            <div className="groupLabel">{renderIntlLabel('channel_label_select_roomtype')}</div>
          </div>
          <div className={styles.roomTypeCon}>
            <FormItem
              label={renderIntlLabel('order_roomorder_occtype')}
              className={styles.occTypeItem}
            >
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={occType}
                onChange={handleOCCTypeChange}
              >
                <Radio.Button value={OccType.DAILY}>
                  {renderIntlLabel('order_option_label_daily')}
                </Radio.Button>
                <Radio.Button value={OccType.HOUR}>
                  {renderIntlLabel('order_option_label_hour')}
                </Radio.Button>
              </Radio.Group>
            </FormItem>
            <br />
            <FormItem label={renderIntlLabel('order_roomorder_arrival_time')}>
              {occType === OccType.DAILY ? (
                <DatePicker
                  placeholder={renderIntlLabel('order_selfService_placeholder_select_createDate')}
                  showTime
                  disabledDate={disabledDate}
                  value={selecteDate}
                  onChange={handleDateChange}
                />
              ) : (
                <DatePicker
                  placeholder={renderIntlLabel('order_selfService_placeholder_select_createDate')}
                  disabledDate={disabledDate}
                  value={selecteDate}
                  onChange={handleDateChange}
                />
              )}
            </FormItem>
            {occType === OccType.DAILY ? (
              <>
                <FormItem
                  label={renderIntlLabel('order_roomorder_checkin_duration_time')}
                  className={styles.rentTimeItem}
                >
                  <NumberWithHelper value={rentTimeNum} onChange={handleDayDurationChange} />
                  <span className={styles.rentTimeUnit}>晚</span>
                </FormItem>
                <FormItem
                  label={renderIntlLabel('order_roomorder_predeparture_time')}
                  className={styles.dayPreOutItem}
                >
                  <span className={styles.outDateWrapper}>
                    {/* {`${moment(selecteDate)
                      .add(rentTimeNum, 'days')
                      .format('YYYY-MM-DD')} 12:00:00`} */}
                    {getDayPreOutItem()}
                  </span>
                </FormItem>
              </>
            ) : null}
          </div>
          <Table
            rowKey="roomId"
            loading={tableLoading}
            columns={occType === OccType.DAILY ? columnsDay : columnsHours}
            // dataSource={[{ createTime: '12' }]}
            dataSource={occType === OccType.DAILY ? dataDayList : dataHoursList}
            pagination={false}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={renderIntlLabel('order_crs_msg_no_data_to_fill_inf_booker_info')}
                />
              ),
            }}
            scroll={{ x: 'max-content' }}
            sticky
          />
        </div>
      </CustomDrawer>

      <ConfirmOrderModal ref={confirmModalRef} onConfirm={handleConfirm} />
    </>
  );
};

const mapStateToProps = (state) => {
  const { userInfo } = state.user;
  return { userInfo };
};

const ConnectComponent: React.FC<any> = connect(
  mapStateToProps,
  {},
)(React.memo(InvitationDetailDrawer));

export default React.forwardRef<any, any>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
